import { useCallback, useLayoutEffect, useRef, useState } from 'react';

/**
 * Where a dropdown list is drawn, for every combobox / autocomplete of the app: PORTALED to <body> with fixed positioning under (or, when
 * there is no room below, over) its input -- so a modal's scrolling body, its footer or a table's scroll container can never clip or cover it.
 * The caller renders the list with `createPortal(<div ref={listRef} className="fixed z-[var(--z-overlay)] ..." style={listStyle}>, document.body)`,
 * puts `anchorRef` on the element the list hangs from, and ignores `listRef` clicks as "inside" in its click-outside check.
 */
export function useAnchoredList(isOpen: boolean) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [listStyle, setListStyle] = useState<{ left: number; width: number; top?: number; bottom?: number } | null>(null);

  const place = useCallback(() => {
    const box = anchorRef.current?.getBoundingClientRect();
    if (!box) return;
    const roomBelow = window.innerHeight - box.bottom;
    // opens upward when there is no room below (the list is at most max-h-60 = 15rem) and more above
    if (roomBelow < 260 && box.top > roomBelow) setListStyle({ left: box.left, width: box.width, bottom: window.innerHeight - box.top + 4 });
    else setListStyle({ left: box.left, width: box.width, top: box.bottom + 4 });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return;
    place();
    window.addEventListener('scroll', place, true); // any scrolling ancestor (a modal body, a table) moves the input
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [isOpen, place]);

  /** true when the event target is inside the portaled list (it is not inside the caller's own container in the DOM) */
  const isInsideList = (target: Node) => !!listRef.current && listRef.current.contains(target);

  return { anchorRef, listRef, listStyle, isInsideList };
}
